import { BettingData } from '@/models/bettingData';
import { FeedLiveItem } from '@/models/feedLive';
import { BetSelection, EventFeedType, EventPeriod } from '@/constants';
import { FeedPregameItem } from '@/models/feedPregame';
import { useEventStore } from '@/stores/event';
import { useUserStore } from '@/stores/user';
import { getBetTypeIdByBetSelection, getHandicapKeyByBetSelection } from '@/helpers';
import { EventMarketTypes } from '@/types/event';

export class BettingDataFactory {
    static createFromFeed(id: number, data: FeedLiveItem | FeedPregameItem, betSelectionId: BetSelection): BettingData {
        const userStore = useUserStore();

        const bettingData = new BettingData();
        bettingData.id = id;
        bettingData.isCreatedFromFeed = true;
        bettingData.betSelectionId = betSelectionId;
        bettingData.customEventId = data.customEventId.value;
        bettingData.leagueName = data.leagueName.value;
        bettingData.leagueId = data.leagueId.value;
        bettingData.iso3 = data.iso3.value;
        bettingData.teamHomeName = data.teamHomeName.value;
        bettingData.teamAwayName = data.teamAwayName.value;
        bettingData.teamHomeLogo = data.teamHomeLogo.value;
        bettingData.teamAwayLogo = data.teamAwayLogo.value;
        bettingData.teamHomeId = data.teamHomeId.value;
        bettingData.teamAwayId = data.teamAwayId.value;
        bettingData.eventStartedAt = data.eventStartedAt.value!;
        bettingData.eventFeedType = data.eventFeedType.value!;
        bettingData.periodTypeId = data.periodTypeId;
        bettingData.betTypeId = getBetTypeIdByBetSelection(bettingData.betSelectionId);
        bettingData.handicapKey = getHandicapKeyByBetSelection(bettingData.betSelectionId);
        bettingData.isActive = true;
        bettingData.uuid = Date.now();
        bettingData.eventScoreForBet = data.getEventScoreForBet;

        bettingData.isTopUps = Number(userStore.settings.bet_order_is_top_ups_default) as 0 | 1;

        if (data instanceof FeedLiveItem) {
            bettingData.scoreHome = data.scoreHome.value;
            bettingData.scoreAway = data.scoreAway.value;
            bettingData.time = data.time.value;
            bettingData.cornersHome = data.cornersHome.value;
            bettingData.cornersAway = data.cornersAway.value;
        } else {
            bettingData.scoreHome = 0;
            bettingData.scoreAway = 0;
            bettingData.time = null;
            bettingData.cornersHome = 0;
            bettingData.cornersAway = 0;
        }

        if (betSelectionId === BetSelection.HandicapHome || betSelectionId === BetSelection.HandicapAway) {
            bettingData.handicap = data.handicap.value;
            bettingData.jollywhere = data.jollywhere.value;
        } else if (betSelectionId === BetSelection.GoalOver || betSelectionId === BetSelection.GoalUnder) {
            bettingData.handicap = data.goalHandicap.value;
            bettingData.jollywhere = null;
        } else {
            bettingData.handicap = null;
            bettingData.jollywhere = null;
        }

        // Extract data based on betSelectionId
        bettingData.updatePriceDataFromFeed(data);

        bettingData.initTimeout();
        bettingData.initStake();
        bettingData.initOdds();

        return bettingData;
    }

    static createFromEvent(market: EventMarketTypes, periodTypeId: EventPeriod, betSelectionId: BetSelection): BettingData {
        const eventStore = useEventStore();
        const userStore = useUserStore();

        const bettingData = new BettingData();
        bettingData.betSelectionId = betSelectionId;
        bettingData.customEventId = eventStore.customEventId!;
        bettingData.leagueName = eventStore.leagueName!;
        bettingData.leagueId = eventStore.leagueId!;
        bettingData.iso3 = eventStore.iso3!;
        bettingData.teamHomeName = eventStore.teamHomeName!;
        bettingData.teamAwayName = eventStore.teamAwayName!;
        bettingData.teamHomeLogo = eventStore.teamHomeLogo!;
        bettingData.teamAwayLogo = eventStore.teamAwayLogo!;
        bettingData.leagueId = eventStore.leagueId!;
        bettingData.teamHomeId = eventStore.teamHomeId!;
        bettingData.teamAwayId = eventStore.teamAwayId!;
        bettingData.eventStartedAt = eventStore.eventStartedAt!;
        bettingData.eventFeedType = eventStore.eventFeedType!;
        bettingData.periodTypeId = periodTypeId;
        bettingData.betTypeId = getBetTypeIdByBetSelection(bettingData.betSelectionId);
        bettingData.handicapKey = getHandicapKeyByBetSelection(bettingData.betSelectionId);
        bettingData.correctScore = 'score' in market ? market.score : null;
        bettingData.isActive = true;
        bettingData.uuid = Date.now();

        bettingData.eventScoreForBet = eventStore.getEventScoreForBet;

        bettingData.isTopUps = Number(userStore.settings.bet_order_is_top_ups_default) as 0 | 1;

        bettingData.cornersHome = eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.cornersHome ?? null) : 0;
        bettingData.cornersAway = eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.cornersAway ?? null) : 0;
        bettingData.scoreHome = eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.scoreHome ?? null) : 0;
        bettingData.scoreAway = eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.scoreAway ?? null) : 0;
        bettingData.time = eventStore.stats?.time ?? null;

        bettingData.handicap = 'handicap' in market ? market.handicap : null;
        bettingData.jollywhere = 'jollywhere' in market ? market.jollywhere : null;

        // Extract data based on betSelectionId
        bettingData.updatePriceDataFromEvent();

        bettingData.initTimeout();
        bettingData.initStake();
        bettingData.initOdds();

        return bettingData;
    }
}
